import { isRedoEmailAddress } from "@redotech/account-common/redo-domains";
import { ReviewingParty } from "@redotech/congo-enums/reviewing-party";
import { isClosedReturnStatus, ReturnStatus } from "../return-status";
import { ReturnedProductStatus } from "../returned-product-status";
import { isClaimUnderRedoReview } from "../reviewing-party";

/** Satisfied by the app's `MerchantAppReturn` and the Mongoose `Return` doc. */
export interface ReturnActionEligibilityReturn {
  status: ReturnStatus;
  reviewingParty?: ReviewingParty | null;
  products: { status: ReturnedProductStatus }[];
}

export interface ReturnActionEligibilityUser {
  email?: string | null;
}

/** Permission checks are deliberately not here; callers compose them. */
export function canResetReturn({
  return_,
}: {
  return_: Pick<ReturnActionEligibilityReturn, "reviewingParty" | "status">;
}): boolean {
  return (
    return_.reviewingParty !== ReviewingParty.Redo &&
    ![ReturnStatus.COMPLETE, ReturnStatus.DELETED].includes(return_.status)
  );
}

export function anyItemsCanBeProcessed(
  return_: Pick<ReturnActionEligibilityReturn, "products">,
): boolean {
  return !return_.products.every(
    (product) =>
      product.status === ReturnedProductStatus.REJECTED ||
      product.status === ReturnedProductStatus.COMPLETE,
  );
}

export function hasEligibleShippingStatus(
  return_: Pick<ReturnActionEligibilityReturn, "status">,
): boolean {
  return [ReturnStatus.DELIVERED, ReturnStatus.IN_TRANSIT].includes(
    return_.status,
  );
}

export function canProcessReturn({
  return_,
}: {
  return_: Pick<ReturnActionEligibilityReturn, "status" | "products">;
}): boolean {
  return (
    (hasEligibleShippingStatus(return_) ||
      [ReturnStatus.OPEN, ReturnStatus.FLAGGED].includes(return_.status)) &&
    anyItemsCanBeProcessed(return_)
  );
}

export function canRejectReturn({
  return_,
  user,
}: {
  return_: ReturnActionEligibilityReturn;
  user?: ReturnActionEligibilityUser | null;
}): boolean {
  const statusCanBeRejected =
    !isClosedReturnStatus(return_.status) &&
    [
      ReturnStatus.OPEN,
      ReturnStatus.NEEDS_REVIEW,
      ReturnStatus.FLAGGED,
    ].includes(return_.status) &&
    anyItemsCanBeProcessed(return_);
  const claimIsManaged = return_.reviewingParty === ReviewingParty.Redo;
  const isRedoAdmin = !!user && isRedoEmailAddress(user.email);
  const canRejectRedoOwnedClaim =
    claimIsManaged &&
    return_.reviewingParty === ReviewingParty.Redo &&
    isRedoAdmin &&
    statusCanBeRejected;
  // Kept exactly as the detail page had it. claimIsManaged already means the
  // reviewing party is Redo, so this branch cannot be true — but this is a
  // relocation, and removing a branch is a behaviour argument that belongs in
  // its own change rather than hidden in a move.
  const canRejectMerchantOwnedClaim =
    claimIsManaged &&
    return_.reviewingParty === ReviewingParty.Merchant &&
    statusCanBeRejected;
  // Merchant-reviewed claims are not "managed" here, so they fall through
  // to the non-managed branch.
  const canRejectNonManagedClaim = !claimIsManaged && statusCanBeRejected;
  return (
    canRejectRedoOwnedClaim ||
    canRejectMerchantOwnedClaim ||
    canRejectNonManagedClaim
  );
}

export function canFlagReturn({
  return_,
}: {
  return_: Pick<ReturnActionEligibilityReturn, "status" | "products">;
}): boolean {
  return (
    (hasEligibleShippingStatus(return_) ||
      return_.status === ReturnStatus.OPEN) &&
    anyItemsCanBeProcessed(return_)
  );
}

/** The inverse of flag: only a flagged return has anything to undo. */
export function canUnflagReturn({
  return_,
}: {
  return_: Pick<ReturnActionEligibilityReturn, "status">;
}): boolean {
  return return_.status === ReturnStatus.FLAGGED;
}

export function canAddTagToReturn({
  return_,
}: {
  return_: Pick<ReturnActionEligibilityReturn, "status">;
}): boolean {
  return return_.status !== ReturnStatus.DELETED;
}

/**
 * Approval answers a return waiting on review. NEEDS_REVIEW sits inside the
 * Redo-review window, so without that guard every Redo claim is offered then skipped.
 */
export function canApproveReturn({
  return_,
  user,
}: {
  return_: Pick<ReturnActionEligibilityReturn, "status" | "reviewingParty">;
  user?: ReturnActionEligibilityUser | null;
}): boolean {
  if (return_.status !== ReturnStatus.NEEDS_REVIEW) {
    return false;
  }
  return !isClaimUnderRedoReview(return_) || isRedoEmailAddress(user?.email);
}

/** Closing is for a return handled outside Redo; a settled one has nothing to close. */
export function canCloseReturn({
  return_,
}: {
  return_: Pick<ReturnActionEligibilityReturn, "status">;
}): boolean {
  return !isClosedReturnStatus(return_.status);
}
